import { call, ForkEffect, put, select, take, takeLatest } from 'redux-saga/effects';
import { Socket } from 'socket.io-client';
import { eventChannel } from 'redux-saga';
import { IGemAiStatusData, IGemMediaDownloadData, IGenerateGemPassportData, websocketActions } from './webSocketSlice';
import { createWebsocketInstance } from '../../api/backendWebsocketInstance';
import { WebSocketOutputTypes } from '../../utils/enums/websocket-output-types.enum';
import { WebSocketStatus } from '../../utils/enums/websocket-status.enum';
import { WebSocketComponentStatus } from '../../utils/enums/websocket-component-status.enum';
import { AiStatus } from '../../utils/enums/gem-ai-status.enum';
import { RootState } from '../store';
import { IGem } from '../gem/gemSlice';

function createWebSocketChannel(socket: Socket<any, any>) {
  return eventChannel((emit) => {
    socket.on('connect', () => {
      emit({ type: WebSocketOutputTypes.CONNECTED });
    });

    socket.on('disconnect', () => {
      emit({ type: WebSocketOutputTypes.DISCONNECTED });
    });

    socket.on('error', (error: any) => {
      emit({ type: WebSocketOutputTypes.ERROR, error });
    });

    socket.on('gem-media-zipped', (data: any) => {
      emit({ type: WebSocketOutputTypes.GEM_MEDIA_ZIPPED, data });
    });

    socket.on('gem-ai-status', (data: any) => {
      emit({ type: WebSocketOutputTypes.GEM_AI_STATUS, data });
    });

    socket.on('gem-passport-generated', (data: any) => {
      emit({ type: WebSocketOutputTypes.GEM_PASSPORT_GENERATED, data });
    });

    // The unsubscribe function to clean up
    return () => {
      socket.disconnect();
    };
  });
}

function* watchConnectWebsocket(): Generator<any, void, any> {
  try {
    const socket = createWebsocketInstance();
    const channel = yield call(createWebSocketChannel, socket);

    while (true) {
      const data = yield take(channel);
      switch (data.type) {
        case WebSocketOutputTypes.CONNECTED:
          yield put(websocketActions.setWebSocketStatus(WebSocketStatus.CONNECTED));
          break;
        case WebSocketOutputTypes.DISCONNECTED:
          yield put(websocketActions.setWebSocketStatus(WebSocketStatus.DISCONNECTED));
          break;
        case WebSocketOutputTypes.GEM_MEDIA_ZIPPED:
          yield put(
            websocketActions.onGemMediaZippedAsync({
              data: {
                gemId: data.data.gemId,
                url: data.data.url,
                loadingStage: data.data.stage,
              },
            }),
          );
          break;
        case WebSocketOutputTypes.GEM_PASSPORT_GENERATED:
          // eslint-disable-next-line no-console
          console.log('Received gem-passport-generated event:', data.data);
          yield put(
            websocketActions.onGemPassportGeneratedAsync({
              data: {
                gemId: data.data.gem.gemId,
                uginId: data.data.gem.aiResponse.uginId,
              },
            }),
          );
          break;
        case WebSocketOutputTypes.GEM_AI_STATUS:
          // eslint-disable-next-line no-console
          console.log('Received gem-ai-status event:', data.data);
          yield put(
            websocketActions.onGetGemAiStatusAsync({
              data: {
                gemId: data.data.gemId,
                aiStatus: data.data.aiStatus,
                aiTryAttempts: data.data.aiTryAttempts,
              },
            }),
          );
          break;
        default:
          break;
      }
    }
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.log('Error:', error);
  }
}

export function* watchOnGemPassportGenerated({ payload }: { payload: { data: IGenerateGemPassportData } }) {
  const { data } = payload;
  // eslint-disable-next-line no-console
  console.log('Received gem-passport-generated event:', data);
  if (data.gemId) {
    yield put(
      websocketActions.setGenerateGemPassportData({
        status: WebSocketComponentStatus.SUCCESS,
        gemId: data.gemId,
        uginId: data.uginId,
      }),
    );
  } else {
    yield put(
      websocketActions.setGenerateGemPassportData({
        status: WebSocketComponentStatus.ERROR,
        gemId: data.gemId,
      }),
    );
  }
}

export function* watchOnGetGemAiStatus({ payload }: { payload: { data: IGemAiStatusData } }) {
  const { data } = payload;
  // eslint-disable-next-line no-console
  console.log('Received gem-passport-generated event:', data);
  const currentGem = (yield select((state: RootState) => state.gem.gem)) as IGem;
  if (data.gemId && currentGem.gemId === data.gemId) {
    yield put(
      websocketActions.setGemAiStatusData({
        aiStatus: data.aiStatus,
        gemId: data.gemId,
        aiTryAttempts: data.aiTryAttempts,
      }),
    );
  }
}

function* watchOnGemZipped({ payload }: { payload: { data: IGemMediaDownloadData } }) {
  const { data } = payload;
  // eslint-disable-next-line no-console
  console.log('Received gem-media-zipped event:', data);
  if (data.loadingStage) {
    yield put(
      websocketActions.setGemMediaDownloadData({
        loadingStage: data.loadingStage,
        status: WebSocketComponentStatus.PENDING,
        gemId: data.gemId,
      }),
    );
    return;
  }
  if (data.url) {
    yield put(
      websocketActions.setGemMediaDownloadData({
        url: data.url,
        status: WebSocketComponentStatus.SUCCESS,
        gemId: data.gemId,
      }),
    );
    return;
  }
  yield put(
    websocketActions.setGemMediaDownloadData({
      url: '',
      status: WebSocketComponentStatus.ERROR,
      gemId: data.gemId,
    }),
  );
}

export function* watchWebsocketSagas(): Generator<ForkEffect, void> {
  yield takeLatest(websocketActions.connectWebSocket, watchConnectWebsocket);
  yield takeLatest(websocketActions.onGemMediaZippedAsync, watchOnGemZipped);
  yield takeLatest(websocketActions.onGetGemAiStatusAsync, watchOnGetGemAiStatus);
  yield takeLatest(websocketActions.onGemPassportGeneratedAsync, watchOnGemPassportGenerated);
}

const websocketSagas = watchWebsocketSagas;
export default websocketSagas;
